import { type Message, type State } from '@ag-ui/core';

import { type ToolCallResultEventParams } from './types/callbacks';
import {
  type FocusNodeListener,
  type FocusNodePayload,
  type HighlightNodeListener,
  type HighlightNodePayload,
  type ListenerEntry,
  type MessagesChangedListener,
  type MessagesChangedPayload,
  type ProgrammaticMessageOptions,
  type StateChangedListener,
  type StateChangedPayload,
  type SubscriptionOptions,
  type ToolCallResultListener,
} from './types/subscriptions';

export class EventSubscriptionManager {
  private focusNodeListeners = new Set<ListenerEntry<FocusNodeListener>>();
  private highlightNodeListeners = new Set<ListenerEntry<HighlightNodeListener>>();
  private messagesChangedListeners = new Set<ListenerEntry<MessagesChangedListener>>();
  private stateChangedListeners = new Set<ListenerEntry<StateChangedListener>>();
  private toolCallResultListeners = new Set<ListenerEntry<ToolCallResultListener>>();

  subscribeToFocusNode(listener: FocusNodeListener, options?: SubscriptionOptions): () => void {
    const entry: ListenerEntry<FocusNodeListener> = {
      listener,
      threadId: options?.threadId,
    };
    this.focusNodeListeners.add(entry);
    return () => {
      this.focusNodeListeners.delete(entry);
    };
  }

  subscribeToMessagesChanged(listener: MessagesChangedListener, options?: SubscriptionOptions): () => void {
    const entry: ListenerEntry<MessagesChangedListener> = {
      listener,
      threadId: options?.threadId,
    };
    this.messagesChangedListeners.add(entry);
    return () => {
      this.messagesChangedListeners.delete(entry);
    };
  }

  subscribeToStateChanged(listener: StateChangedListener, options?: SubscriptionOptions): () => void {
    const entry: ListenerEntry<StateChangedListener> = {
      listener,
      threadId: options?.threadId,
    };
    this.stateChangedListeners.add(entry);
    return () => {
      this.stateChangedListeners.delete(entry);
    };
  }

  subscribeToToolCallResult(listener: ToolCallResultListener, options?: SubscriptionOptions): () => void {
    const entry: ListenerEntry<ToolCallResultListener> = {
      listener,
      threadId: options?.threadId,
    };
    this.toolCallResultListeners.add(entry);
    return () => {
      this.toolCallResultListeners.delete(entry);
    };
  }

  subscribeToHighlightNode(listener: HighlightNodeListener, options?: SubscriptionOptions): () => void {
    const entry: ListenerEntry<HighlightNodeListener> = {
      listener,
      threadId: options?.threadId,
    };
    this.highlightNodeListeners.add(entry);
    return () => {
      this.highlightNodeListeners.delete(entry);
    };
  }

  notifyHighlightNode(payload: HighlightNodePayload): void {
    const listeners = Array.from(this.highlightNodeListeners);
    for (const { listener } of listeners) {
      listener(payload);
    }
  }

  notifyFocusNode(runId: string): void {
    const payload: FocusNodePayload = { runId };
    const listeners = Array.from(this.focusNodeListeners);
    for (const { listener } of listeners) {
      listener(payload);
    }
  }

  notifyMessagesChanged(threadId: string, messages: Message[]): void {
    const payload: MessagesChangedPayload = { messages, threadId };
    // Snapshot to protect against modifications during iteration
    const listeners = Array.from(this.messagesChangedListeners);
    for (const { listener, threadId: filterThreadId } of listeners) {
      if (!filterThreadId || filterThreadId === threadId) {
        listener(payload);
      }
    }
  }

  notifyStateChanged(threadId: string, state: State): void {
    const payload: StateChangedPayload = { state, threadId };
    // Snapshot to protect against modifications during iteration
    const listeners = Array.from(this.stateChangedListeners);
    for (const { listener, threadId: filterThreadId } of listeners) {
      if (!filterThreadId || filterThreadId === threadId) {
        listener(payload);
      }
    }
  }

  notifyToolCallResult(
    threadId: string,
    params: { programmaticOptions?: ProgrammaticMessageOptions } & ToolCallResultEventParams,
  ): void {
    const payload = { ...params, threadId };
    // Snapshot to protect against modifications during iteration
    const listeners = Array.from(this.toolCallResultListeners);
    for (const { listener, threadId: filterThreadId } of listeners) {
      if (!filterThreadId || filterThreadId === threadId) {
        listener(payload);
      }
    }
  }

  dispose(): void {
    this.focusNodeListeners.clear();
    this.highlightNodeListeners.clear();
    this.messagesChangedListeners.clear();
    this.stateChangedListeners.clear();
    this.toolCallResultListeners.clear();
  }
}
